import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  X, Move, Image as ImageIcon, FileText, Maximize2, Minimize2,
  Shuffle, Undo2, Trash2, Star, Grid3x3, Pencil, LayoutGrid, RotateCw
} from 'lucide-react';
import { adminImages } from './imageData';
import {
  PACKS, EXERCISES, DEFAULT_PACK, getExercise, getPack, slotColumns
} from './exercises';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import './vista.css';

const STORE_KEY = 'vista-kepkartya-v1';
const FAV_KEY = 'vista-kepkartya-kedvencek-v1';
const MAX_FREE = 12;
/* A lap alapmérete: kis táblán (mobil, beágyazott iframe) a korábbi 260 px
   kilógott a keretből, és a sarok-fogantyúk levágódtak. */
const DEFAULT_CARD = 130;
const EMPTY_BOARD = { pieces: [], slots: {} };

/* A galéria kis képeket tölt (kepek-thumb/*.webp), a tábla és a mentés
   a teljes felbontású eredetit. Ha a kicsinyített változat hiányzik,
   az onError visszaesik az eredetire. */
const thumbSrc = (src) =>
  src.replace('/kepek/', '/kepek-thumb/').replace(/\.(jpe?g|png|webp)$/i, '.webp');

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

function loadStored() {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch (err) {
    return null;
  }
}

const App = () => {
  const stored = useMemo(loadStored, []);

  const [packKey, setPackKey] = useState(stored?.packKey || DEFAULT_PACK);
  const [exerciseId, setExerciseId] = useState(stored?.exerciseId || 'free');
  const [board, setBoard] = useState(stored?.board || EMPTY_BOARD);
  const [selected, setSelected] = useState(null);
  const [past, setPast] = useState([]);
  const [toast, setToast] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [boardWidth, setBoardWidth] = useState(900);
  const [editingNote, setEditingNote] = useState(null);
  const [favorites, setFavorites] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem(FAV_KEY) || '[]'));
    } catch (err) {
      return new Set();
    }
  });
  const [onlyFavorites, setOnlyFavorites] = useState(false);
  const [spreadOpen, setSpreadOpen] = useState(false);
  const [spreadPicks, setSpreadPicks] = useState([]);   // a Kiterítve nézetben kijelölt lapok
  const [editingLabel, setEditingLabel] = useState(null);

  const boardRef = useRef(null);
  const ghostRef = useRef(null);
  const dragRef = useRef(null);
  const toastTimer = useRef(null);

  const exercise = getExercise(exerciseId);
  const pack = getPack(packKey);
  const isSlotMode = Array.isArray(exercise.slots);

  /* ---------- kártyakészlet ---------- */

  const packCards = useMemo(
    () => adminImages.filter((img) => img.src.includes(`/kepek/${pack.dir}/`)),
    [pack.dir]
  );

  /* A galéria szűrhető a megcsillagozott lapokra. A véletlen húzás és a
     „Kiterítve" nézet mindig a TELJES csomagból dolgozik — a szűrő csak
     nézet, nem szűkíti a készletet. */
  const cards = useMemo(
    () => (onlyFavorites ? packCards.filter((img) => favorites.has(img.src)) : packCards),
    [packCards, onlyFavorites, favorites]
  );

  const favCount = useMemo(
    () => packCards.filter((img) => favorites.has(img.src)).length,
    [packCards, favorites]
  );

  const toggleFavorite = useCallback((src) => {
    setFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(src)) next.delete(src);
      else next.add(src);
      try {
        localStorage.setItem(FAV_KEY, JSON.stringify([...next]));
      } catch (err) {
        /* privát böngészés: a kedvencek nem maradnak meg, a munka attól még megy */
      }
      return next;
    });
  }, []);

  /* ---------- megőrzés ---------- */

  useEffect(() => {
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify({ packKey, exerciseId, board }));
    } catch (err) {
      /* privát böngészés: a mentés kimarad, a munka attól még megy */
    }
  }, [packKey, exerciseId, board]);

  const showToast = useCallback((text) => {
    setToast(text);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(''), 2200);
  }, []);

  const commit = useCallback((next) => {
    setPast((prev) => [...prev.slice(-29), board]);
    setBoard(next);
  }, [board]);

  const undo = useCallback(() => {
    if (!past.length) return;
    setBoard(past[past.length - 1]);
    setPast(past.slice(0, -1));
  }, [past]);

  /* ---------- tábla mérete a sablon-rácshoz ---------- */

  useEffect(() => {
    const el = boardRef.current;
    if (!el) return undefined;
    const measure = () => setBoardWidth(el.clientWidth || 900);
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /* Amíg a Kiterítve nyitva van, a mögötte lévő galéria és csomagsor görgetése
     zárva — különben két görgetősáv látszik egyszerre, és az egér a háttérben
     lévő listát mozgatja a kiterített lapok helyett. */
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('spread-open', spreadOpen);
    return () => root.classList.remove('spread-open');
  }, [spreadOpen]);

  /* ---------- teljes képernyő ---------- */

  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggleFullscreen = () => {
    const el = document.documentElement;
    if (!document.fullscreenElement) {
      const req = el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen;
      if (req) req.call(el);
    } else {
      const exit = document.exitFullscreen || document.webkitExitFullscreen;
      if (exit) exit.call(document);
    }
  };

  /* ---------- elhelyezés ---------- */

  const placeInSlot = useCallback((index, image) => {
    commit({
      ...board,
      slots: { ...board.slots, [index]: { src: image.src, note: board.slots[index]?.note || '' } }
    });
  }, [board, commit]);

  const placeFree = useCallback((image, x, y) => {
    if (board.pieces.length >= MAX_FREE) {
      showToast(`Legfeljebb ${MAX_FREE} lap fér a táblára`);
      return;
    }
    const el = boardRef.current;
    const w = DEFAULT_CARD;
    const h = DEFAULT_CARD;
    const maxX = Math.max(0, (el?.clientWidth || 800) - w);
    const maxY = Math.max(0, (el?.clientHeight || 600) - h);
    const top = board.pieces.reduce((m, p) => Math.max(m, p.z), 0);
    commit({
      ...board,
      pieces: [...board.pieces, {
        id: `${image.id}-${Date.now()}`,
        src: image.src,
        x: clamp(x - w / 2, 0, maxX),
        y: clamp(y - h / 2, 0, maxY),
        w,
        h,
        z: top + 1,
        note: ''
      }]
    });
  }, [board, commit, showToast]);

  /* Kattintás a táblára / kártyahelyre: a kijelölt lap odakerül.
     Így nem csak húzással lehet dolgozni. */
  const handleBoardClick = (event) => {
    if (!selected) return;
    const el = boardRef.current;
    const rect = el.getBoundingClientRect();
    if (isSlotMode) {
      const frame = event.target.closest('.slot-frame');
      if (!frame) return;
      placeInSlot(Number(frame.dataset.slot), selected);
    } else {
      placeFree(selected, event.clientX - rect.left, event.clientY - rect.top);
    }
    setSelected(null);
  };

  /* ---------- húzás a galériából ---------- */

  const clearHot = () => {
    document.querySelectorAll('.slot-frame.hot').forEach((n) => n.classList.remove('hot'));
    boardRef.current?.classList.remove('drop-active');
  };

  /* Érintésnél a húzás csak rövid nyomva tartás után indul — különben a
     galéria függőleges görgetése azonnal húzásnak látszana. Egérrel
     nincs várakozás, ott elég a néhány pixeles küszöb. */
  const onThumbPointerDown = (event, image) => {
    if (event.button !== undefined && event.button !== 0) return;
    const touch = event.pointerType === 'touch';
    const drag = {
      image,
      startX: event.clientX,
      startY: event.clientY,
      moved: false,
      armed: !touch,
      timer: null
    };
    if (touch) {
      drag.timer = setTimeout(() => { drag.armed = true; }, 220);
    }
    dragRef.current = drag;
  };

  useEffect(() => {
    const onMove = (event) => {
      const drag = dragRef.current;
      if (!drag) return;
      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;
      const dist = Math.hypot(dx, dy);

      /* Még nem élesedett a húzás: ha közben elmozdul, az görgetés — elengedjük. */
      if (!drag.armed) {
        if (dist > 10) {
          clearTimeout(drag.timer);
          dragRef.current = null;
        }
        return;
      }
      if (!drag.moved && dist < 7) return;

      if (!drag.moved) {
        drag.moved = true;
        const ghost = ghostRef.current;
        if (ghost) {
          ghost.style.display = 'block';
          ghost.querySelector('img').src = thumbSrc(drag.image.src);
        }
      }
      event.preventDefault();

      const ghost = ghostRef.current;
      if (ghost) {
        ghost.style.left = `${event.clientX}px`;
        ghost.style.top = `${event.clientY}px`;
      }

      clearHot();
      const under = document.elementFromPoint(event.clientX, event.clientY);
      const frame = under?.closest?.('.slot-frame');
      if (frame) frame.classList.add('hot');
      else if (under?.closest?.('.board')) boardRef.current?.classList.add('drop-active');
    };

    const onUp = (event) => {
      const drag = dragRef.current;
      dragRef.current = null;
      if (!drag) return;
      clearTimeout(drag.timer);
      if (ghostRef.current) ghostRef.current.style.display = 'none';
      clearHot();

      if (!drag.moved) {
        setSelected((prev) => (prev && prev.src === drag.image.src ? null : drag.image));
        return;
      }

      const under = document.elementFromPoint(event.clientX, event.clientY);
      if (!under) return;
      const frame = under.closest?.('.slot-frame');
      if (frame) {
        placeInSlot(Number(frame.dataset.slot), drag.image);
        return;
      }
      if (!isSlotMode && under.closest?.('.board')) {
        const rect = boardRef.current.getBoundingClientRect();
        placeFree(drag.image, event.clientX - rect.left, event.clientY - rect.top);
      }
    };

    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [isSlotMode, placeFree, placeInSlot]);

  /* ---------- lapok mozgatása és méretezése a szabad táblán ---------- */

  /* Szabad forgatás a fogantyúval: a szöget a lap középpontjához mért irány adja,
     így a lap az egeret követi. Shift = 15 fokos raszter, dupla kattintás = vissza 0-ra. */
  const startPieceRotate = (event, piece) => {
    event.stopPropagation();
    event.preventDefault();
    const el = boardRef.current;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + piece.x + piece.w / 2;
    const cy = rect.top + piece.y + piece.h / 2;
    const angleOf = (ev) => (Math.atan2(ev.clientY - cy, ev.clientX - cx) * 180) / Math.PI;
    const startAngle = angleOf(event);
    const startRot = piece.rot || 0;
    const top = board.pieces.reduce((m, p) => Math.max(m, p.z), 0);

    const target = event.currentTarget;
    if (target.setPointerCapture) target.setPointerCapture(event.pointerId);

    let latest = board.pieces;
    const move = (ev) => {
      ev.preventDefault();
      let rot = startRot + (angleOf(ev) - startAngle);
      rot = ev.shiftKey ? Math.round(rot / 15) * 15 : Math.round(rot);
      latest = latest.map((p) => (p.id === piece.id ? { ...p, rot, z: top + 1 } : p));
      setBoard((prev) => ({ ...prev, pieces: latest }));
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      setPast((prev) => [...prev.slice(-29), board]);
    };
    window.addEventListener('pointermove', move, { passive: false });
    window.addEventListener('pointerup', up);
  };

  const resetPieceRotation = (piece) => {
    commit({
      ...board,
      pieces: board.pieces.map((p) => (p.id === piece.id ? { ...p, rot: 0 } : p))
    });
  };

  const startPieceDrag = (event, piece, mode) => {
    event.stopPropagation();
    event.preventDefault();
    const el = boardRef.current;
    const rect = el.getBoundingClientRect();
    const offsetX = event.clientX - rect.left - piece.x;
    const offsetY = event.clientY - rect.top - piece.y;
    const startW = piece.w;
    const startH = piece.h;
    const startX = event.clientX;
    const startY = event.clientY;
    const top = board.pieces.reduce((m, p) => Math.max(m, p.z), 0);

    let latest = board.pieces;
    const target = event.currentTarget;
    if (target.setPointerCapture) target.setPointerCapture(event.pointerId);

    const move = (ev) => {
      ev.preventDefault();
      latest = latest.map((p) => {
        if (p.id !== piece.id) return p;
        if (mode === 'resize') {
          const w = clamp(startW + (ev.clientX - startX), 80, rect.width);
          const h = clamp(startH + (ev.clientY - startY), 70, rect.height);
          return { ...p, w, h, z: top + 1 };
        }
        return {
          ...p,
          x: clamp(ev.clientX - rect.left - offsetX, 0, rect.width - p.w),
          y: clamp(ev.clientY - rect.top - offsetY, 0, rect.height - p.h),
          z: top + 1
        };
      });
      setBoard((prev) => ({ ...prev, pieces: latest }));
    };

    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      setPast((prev) => [...prev.slice(-29), board]);
    };

    window.addEventListener('pointermove', move, { passive: false });
    window.addEventListener('pointerup', up);
  };

  /* ---------- műveletek ---------- */

  const drawRandom = () => {
    if (!packCards.length) return;
    const pick = () => packCards[Math.floor(Math.random() * packCards.length)];
    if (isSlotMode) {
      const slots = {};
      exercise.slots.forEach((_, i) => {
        slots[i] = { src: pick().src, note: board.slots[i]?.note || '' };
      });
      commit({ ...board, slots });
      showToast(exercise.slots.length > 1 ? `${exercise.slots.length} lap húzva` : 'Lap húzva');
      return;
    }
    const el = boardRef.current;
    const w = DEFAULT_CARD;
    const cw = el?.clientWidth || 800;
    const ch = el?.clientHeight || 600;
    const count = Math.min(exercise.draw, MAX_FREE - board.pieces.length);
    if (count <= 0) {
      showToast(`Legfeljebb ${MAX_FREE} lap fér a táblára`);
      return;
    }
    const top = board.pieces.reduce((m, p) => Math.max(m, p.z), 0);
    const added = Array.from({ length: count }, (_, i) => {
      const image = pick();
      return {
        id: `${image.id}-${Date.now()}-${i}`,
        src: image.src,
        x: clamp(cw / 2 - w / 2 + (i - count / 2) * 46, 0, Math.max(0, cw - w)),
        y: clamp(ch / 2 - w / 2 + (i % 2) * 32, 0, Math.max(0, ch - w)),
        w,
        h: w,
        z: top + 1 + i,
        note: ''
      };
    });
    commit({ ...board, pieces: [...board.pieces, ...added] });
    showToast(count > 1 ? `${count} lap húzva` : 'Lap húzva');
  };

  /* A „Kiterítve" nézetben kiválasztott lapokat dizájnos, véletlen elrendezésben
     teszi a táblára: rács-alapra ültetve, de elcsúsztatva és enyhén elforgatva,
     hogy a sarkoknál átfedjenek — mint az asztalra dobott fotók. Minden lap
     elfér a táblán (fit to page); a méret a lapszámhoz igazodik. */
  const arrangeOnBoard = (picks) => {
    const el = boardRef.current;
    if (!el || !picks.length) return;
    const W = el.clientWidth;
    const H = el.clientHeight;

    /* A képarányokat a már betöltött előnézetekből vesszük, hogy az álló és
       fekvő lapok ne négyzetre gyömöszölve kerüljenek ki. */
    const ratios = new Map();
    document.querySelectorAll('.spread-grid .thumb').forEach((t) => {
      const img = t.querySelector('img');
      if (img && img.naturalWidth && t.dataset.src) {
        ratios.set(t.dataset.src, img.naturalWidth / img.naturalHeight);
      }
    });

    const n = picks.length;
    const cols = Math.max(1, Math.round(Math.sqrt((n * W) / H)));
    const rows = Math.ceil(n / cols);
    const cw = W / cols;
    const ch = H / rows;
    const base = Math.min(cw, ch) * 1.16;          // 16%: ennyi az átfedés a cellahatáron

    const shuffled = [...picks].sort(() => Math.random() - 0.5);
    const pieces = shuffled.map((image, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const ar = ratios.get(image.src) || 1;
      const w = ar >= 1 ? base : base * ar;
      const h = ar >= 1 ? base / ar : base;
      const rot = (Math.random() - 0.5) * 13;      // −6,5°..+6,5°
      /* A forgatás megnöveli a helyigényt; ennyi tartalékkal marad a táblán. */
      const pad = Math.max(w, h) * 0.06;
      const cx = (col + 0.5) * cw + (Math.random() - 0.5) * cw * 0.24;
      const cy = (row + 0.5) * ch + (Math.random() - 0.5) * ch * 0.24;
      return {
        id: `${image.id}-${Date.now()}-${i}`,
        src: image.src,
        x: clamp(cx - w / 2, pad, Math.max(pad, W - w - pad)),
        y: clamp(cy - h / 2, pad, Math.max(pad, H - h - pad)),
        w,
        h,
        rot,
        z: i + 1,
        note: ''
      };
    });

    setExerciseId('free');
    commit({ pieces, slots: {}, labels: {} });
    setSpreadPicks([]);
    setSpreadOpen(false);
    setSelected(null);
    showToast(`${n} lap elrendezve`);
  };

  const clearBoard = () => {
    if (!board.pieces.length && !Object.keys(board.slots).length) return;
    commit(EMPTY_BOARD);
    setSelected(null);
  };

  const changeExercise = (id) => {
    const next = getExercise(id);
    setExerciseId(id);
    setSelected(null);
    commit(EMPTY_BOARD);
    if (next.pack) setPackKey(next.pack);
  };

  const setNote = (kind, key, value) => {
    if (kind === 'slot') {
      const current = board.slots[key];
      if (!current) return;
      setBoard({ ...board, slots: { ...board.slots, [key]: { ...current, note: value } } });
    } else {
      setBoard({
        ...board,
        pieces: board.pieces.map((p) => (p.id === key ? { ...p, note: value } : p))
      });
    }
  };

  const removeSlot = (index) => {
    const slots = { ...board.slots };
    delete slots[index];
    commit({ ...board, slots });
  };

  const removePiece = (id) => {
    commit({ ...board, pieces: board.pieces.filter((p) => p.id !== id) });
  };

  /* ---------- mentés ---------- */

  const hasContent = board.pieces.length > 0 || Object.keys(board.slots).length > 0;

  /* A html2canvas nem érti a CSS Gridet és a flexet: a rácsba rendezett
     kártyahelyeket egymásra csúsztatva rajzolná ki. Ezért mentés előtt
     minden elemet a ténylegesen kimért helyére rögzítünk (a szülőjéhez
     képest), utána visszaállítjuk. A mérés és az alkalmazás külön körben
     fut, különben az első átállítás elmozdítaná a többi elemet. */
  const flattenLayout = (root) => {
    const nodes = Array.from(root.querySelectorAll('*')).filter(
      (n) => n.tagName !== 'IMG'
        && !n.closest('svg')
        && window.getComputedStyle(n).display !== 'none'
    );
    const measured = nodes.map((node) => {
      const rect = node.getBoundingClientRect();
      const parentRect = node.parentElement.getBoundingClientRect();
      return {
        node,
        left: rect.left - parentRect.left,
        top: rect.top - parentRect.top,
        width: rect.width,
        height: rect.height,
        saved: node.getAttribute('style') || ''
      };
    });
    measured.forEach(({ node, left, top, width, height }) => {
      node.style.position = 'absolute';
      node.style.left = `${left}px`;
      node.style.top = `${top}px`;
      node.style.width = `${width}px`;
      node.style.height = `${height}px`;
      node.style.margin = '0';
      node.style.maxWidth = 'none';
      node.style.maxHeight = 'none';
      node.style.minWidth = '0';
      node.style.minHeight = '0';
      node.style.flex = 'none';
      /* A képet tartó doboz block legyen: a beégetett méretet margóval
         igazítjuk középre, amit a flex középre-igazítása megduplázna. */
      if (node.classList.contains('slot-frame') || node.classList.contains('piece')) {
        node.style.display = 'block';
      }
    });
    return () => measured.forEach(({ node, saved }) => {
      if (saved) node.setAttribute('style', saved);
      else node.removeAttribute('style');
    });
  };

  const exportBoard = async (kind) => {
    const el = boardRef.current;
    if (!el || !hasContent) return;

    setEditingNote(null);
    el.classList.add('exporting');
    /* A flatten a grid-elrendezés miatt kell; a szabad táblán minden lap már
       abszolút pozicionált, és ott a forgatás miatt a bounding box hamis értéket adna. */
    const restoreLayout = isSlotMode ? flattenLayout(el) : () => {};

    /* Az object-fit: contain méretét kézzel „bele kell égetni" a képekbe,
       különben a html2canvas kinyújtja őket. */
    const images = el.querySelectorAll('.slot-frame img, .piece img');
    const saved = [];
    images.forEach((img) => {
      const parent = img.parentElement;
      const rect = parent.getBoundingClientRect();
      saved.push({
        img,
        width: img.style.width,
        height: img.style.height,
        marginTop: img.style.marginTop,
        marginLeft: img.style.marginLeft,
        objectFit: img.style.objectFit
      });
      const nw = img.naturalWidth;
      const nh = img.naturalHeight;
      if (!nw || !nh) return;
      const ratio = nw / nh;
      const parentRatio = rect.width / rect.height;
      let w;
      let h;
      if (ratio > parentRatio) {
        w = rect.width;
        h = rect.width / ratio;
      } else {
        h = rect.height;
        w = rect.height * ratio;
      }
      img.style.width = `${w}px`;
      img.style.height = `${h}px`;
      img.style.objectFit = 'fill';
      img.style.marginTop = `${(rect.height - h) / 2}px`;
      img.style.marginLeft = `${(rect.width - w) / 2}px`;
    });

    try {
      /* windowWidth/windowHeight nélkül: azzal a html2canvas a klónozott
         oldalt szűkebb ablakban méretezné újra, és a tábla tartalma
         balra zsugorodva, a vászon jobb harmada üresen kerülne a képre. */
      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#10343b'
      });

      const name = exercise.id === 'free' ? 'montazs' : exercise.id;
      if (kind === 'pdf') {
        const pdf = new jsPDF({
          orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
          unit: 'px',
          format: [canvas.width, canvas.height]
        });
        pdf.addImage(canvas.toDataURL('image/jpeg', 0.92), 'JPEG', 0, 0, canvas.width, canvas.height);
        pdf.save(`kepkartya-${name}.pdf`);
      } else {
        const link = document.createElement('a');
        link.download = `kepkartya-${name}.jpg`;
        link.href = canvas.toDataURL('image/jpeg', 0.92);
        link.click();
      }
      showToast(kind === 'pdf' ? 'PDF letöltve' : 'Kép letöltve');
    } catch (err) {
      showToast('A mentés nem sikerült');
    } finally {
      saved.forEach((entry) => {
        entry.img.style.width = entry.width;
        entry.img.style.height = entry.height;
        entry.img.style.marginTop = entry.marginTop;
        entry.img.style.marginLeft = entry.marginLeft;
        entry.img.style.objectFit = entry.objectFit;
      });
      restoreLayout();
      el.classList.remove('exporting');
    }
  };

  /* ---------- megjelenítés ---------- */

  const columns = isSlotMode ? slotColumns(exercise.slots.length, boardWidth) : 1;

  const renderNote = (kind, key, value) => {
    const active = editingNote && editingNote.kind === kind && editingNote.key === key;
    if (active) {
      return (
        <input
          className="note-input"
          autoFocus
          defaultValue={value}
          maxLength={90}
          onBlur={(e) => { setNote(kind, key, e.target.value); setEditingNote(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
          onPointerDown={(e) => e.stopPropagation()}
        />
      );
    }
    /* Az „empty” osztályt a mentés elrejti: üres jegyzetnél a felszólítás
       ne kerüljön rá a képre és a PDF-re. */
    return (
      <div
        className={`note-input${value ? '' : ' empty'}`}
        role="button"
        tabIndex={0}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); setEditingNote({ kind, key }); }}
        onKeyDown={(e) => { if (e.key === 'Enter') setEditingNote({ kind, key }); }}
      >
        {value || <Pencil size={12} className="note-hint" />}
      </div>
    );
  };

  /* A sablon kártyahelyeinek felirata szerkeszthető: a saját címke a táblán
     marad (mentésbe is), a gyakorlat váltása visszaállítja az eredetit. */
  const slotLabel = (index) =>
    (board.labels && board.labels[index] !== undefined)
      ? board.labels[index]
      : (isSlotMode ? exercise.slots[index] : '');

  const setSlotLabel = (index, text) => {
    const val = text.trim();
    const labels = { ...(board.labels || {}) };
    if (!val || val === exercise.slots[index]) delete labels[index];
    else labels[index] = val;
    setBoard({ ...board, labels });
  };

  return (
    <div className="app">
      <div className="topbar">
        <span className="topbar-title">
          <span className="mark" />
          <span className="name">Asszociációs képkártyák</span>
        </span>

        <label className="sr-only" htmlFor="exercise">Gyakorlat</label>
        <select
          id="exercise"
          className="exercise-select"
          value={exerciseId}
          onChange={(e) => changeExercise(e.target.value)}
        >
          {EXERCISES.map((ex) => (
            <option key={ex.id} value={ex.id}>{ex.name}</option>
          ))}
        </select>

        <button className="btn btn-draw" onClick={drawRandom} title="Véletlen lap húzása a nyitott csomagból">
          <Shuffle size={16} /> <span className="btn-label">Véletlen húzás</span>
        </button>
        <button className="btn btn-icon" onClick={undo} disabled={!past.length} title="Visszavonás">
          <Undo2 size={16} />
        </button>
        <button className="btn btn-icon" onClick={clearBoard} disabled={!hasContent} title="Tábla ürítése">
          <Trash2 size={16} />
        </button>
        <button className="btn btn-icon" onClick={() => exportBoard('png')} disabled={!hasContent} title="Mentés képként">
          <ImageIcon size={16} />
        </button>
        <button className="btn btn-icon" onClick={() => exportBoard('pdf')} disabled={!hasContent} title="Mentés PDF-ként">
          <FileText size={16} />
        </button>
        <button
          className="btn btn-icon"
          onClick={toggleFullscreen}
          title={isFullscreen ? 'Kilépés a teljes képernyőből' : 'Teljes képernyő'}
        >
          {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
        </button>
      </div>

      <div className="packs">
        <span className="packs-label">Kártyacsomag</span>
        {PACKS.map((p) => {
          const count = adminImages.filter((img) => img.src.includes(`/kepek/${p.dir}/`)).length;
          return (
            <button
              key={p.key}
              className="pack-chip"
              aria-pressed={p.key === packKey}
              onClick={() => { setPackKey(p.key); setSelected(null); }}
            >
              {p.name}<span className="count">{count}</span>
            </button>
          );
        })}
      </div>

      <div className="main">
        <div className="board-wrap">
          <p className="board-prompt">
            <b>{exercise.name}</b>
            <span>{exercise.prompt}</span>
          </p>

          <div className="board" ref={boardRef} onClick={handleBoardClick}>
            {isSlotMode ? (
              <div className="slots" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
                {exercise.slots.map((_orig, index) => {
                  const filled = board.slots[index];
                  const label = slotLabel(index);
                  const editing = editingLabel === index;
                  return (
                    <div className="slot" key={`${exercise.id}-${index}`}>
                      {editing ? (
                        <input
                          className="slot-label-input"
                          defaultValue={label}
                          maxLength={40}
                          autoFocus
                          /* Windows-os átnevezés-érzet: a szöveg kijelölve, kurzorral */
                          onFocus={(e) => e.target.select()}
                          onBlur={(e) => { setSlotLabel(index, e.target.value); setEditingLabel(null); }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') e.target.blur();
                            if (e.key === 'Escape') { e.target.value = label; e.target.blur(); }
                          }}
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <span className="slot-label">
                          {label}
                          <button
                            className="label-edit"
                            title="Felirat átírása"
                            aria-label={`${label} felirat átírása`}
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={(e) => { e.stopPropagation(); setEditingLabel(index); }}
                          >
                            <Pencil size={12} />
                          </button>
                        </span>
                      )}
                      <div className="slot-body">
                      <div
                        className={`slot-frame${filled ? ' filled' : ''}`}
                        data-slot={index}
                        role="button"
                        tabIndex={0}
                        aria-label={`${label || (index + 1) + '.'} — kártyahely`}
                        onKeyDown={(e) => {
                          if ((e.key === 'Enter' || e.key === ' ') && selected) {
                            e.preventDefault();
                            placeInSlot(index, selected);
                            setSelected(null);
                          }
                        }}
                      >
                        {filled ? (
                          <>
                            <img src={filled.src} alt={label} />
                            <button
                              className="slot-clear"
                              onClick={(e) => { e.stopPropagation(); removeSlot(index); }}
                              aria-label={`${label} lap levétele`}
                            >
                              <X size={13} />
                            </button>
                          </>
                        ) : (
                          <span className="plus">+</span>
                        )}
                      </div>
                        {filled ? renderNote('slot', index, filled.note) : <span style={{ height: 26 }} />}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <>
                {board.pieces.length === 0 && (
                  <div className="board-empty">
                    <strong>Válassz egy lapot a galériából</strong>
                    <span>
                      Koppints rá, majd a táblára — vagy húzd át. Legfeljebb {MAX_FREE} lap fér ide egyszerre.
                    </span>
                  </div>
                )}
                {board.pieces.map((piece) => (
                  <div
                    key={piece.id}
                    className="piece"
                    style={{
                      left: piece.x, top: piece.y, width: piece.w, height: piece.h, zIndex: piece.z,
                      transform: piece.rot ? `rotate(${piece.rot}deg)` : undefined
                    }}
                    onPointerDown={(e) => startPieceDrag(e, piece, 'move')}
                  >
                    <img src={piece.src} alt="" draggable={false} />
                    <button
                      className="piece-remove"
                      onClick={(e) => { e.stopPropagation(); removePiece(piece.id); }}
                      onPointerDown={(e) => e.stopPropagation()}
                      aria-label="Lap levétele"
                    >
                      <X size={13} />
                    </button>
                    <div
                      className="piece-rotate"
                      title="Forgatás — Shift: 15 fokonként, dupla kattintás: vissza egyenesbe"
                      onPointerDown={(e) => startPieceRotate(e, piece)}
                      onDoubleClick={(e) => { e.stopPropagation(); resetPieceRotation(piece); }}
                      aria-hidden="true"
                    >
                      <RotateCw size={12} />
                    </div>
                    <div
                      className="piece-resize"
                      title="Átméretezés"
                      onPointerDown={(e) => startPieceDrag(e, piece, 'resize')}
                      aria-hidden="true"
                    >
                      <Move size={12} />
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>

        <div className="gallery">
          <div className="gallery-head">
            <b>{pack.name}</b>
            <span className="gallery-tools">
              <button
                className="link-btn"
                onClick={() => setSpreadOpen(true)}
                title="Az egész csomag egy képernyőn"
              >
                <Grid3x3 size={14} /> Kiterítve
              </button>
              <button
                className="link-btn"
                aria-pressed={onlyFavorites}
                disabled={!favCount && !onlyFavorites}
                onClick={() => setOnlyFavorites((v) => !v)}
                title={onlyFavorites ? 'Minden lap mutatása' : 'Csak a csillagozott lapok'}
              >
                <Star size={14} fill={onlyFavorites ? 'currentColor' : 'none'} /> {favCount}
              </button>
              <span className="count">{cards.length} lap</span>
            </span>
          </div>
          <div className="grid">
            {cards.map((image, index) => (
              <button
                key={image.id}
                className="thumb"
                aria-pressed={selected?.src === image.src}
                aria-label={`${pack.name} kártya, ${index + 1}.`}
                onPointerDown={(e) => onThumbPointerDown(e, image)}
              >
                <img
                  src={thumbSrc(image.src)}
                  alt=""
                  loading="lazy"
                  draggable={false}
                  onError={(e) => { if (e.target.src !== image.src) e.target.src = image.src; }}
                />
                <span
                  className={`fav${favorites.has(image.src) ? ' on' : ''}`}
                  role="button"
                  tabIndex={-1}
                  aria-label="Csillagozás"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => { e.stopPropagation(); toggleFavorite(image.src); }}
                >
                  <Star size={13} fill={favorites.has(image.src) ? 'currentColor' : 'none'} />
                </span>
              </button>
            ))}
            {cards.length === 0 && (
              <p className="grid-empty">Még nincs csillagozott lap ebben a csomagban.</p>
            )}
          </div>
          <p className="gallery-hint">
            {selected ? 'Most koppints a táblára, ahová tenni szeretnéd.' : 'Koppints egy lapra, vagy húzd át a táblára.'}
          </p>
        </div>
      </div>

      <div
        ref={ghostRef}
        style={{
          display: 'none',
          position: 'fixed',
          zIndex: 3000,
          width: 96,
          height: 96,
          marginLeft: -48,
          marginTop: -48,
          pointerEvents: 'none',
          borderRadius: 10,
          overflow: 'hidden',
          boxShadow: '0 10px 28px rgba(2,12,15,.45)',
          opacity: 0.9
        }}
        aria-hidden="true"
      >
        <img alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </div>

      {/* „Kiterítve" — a lecke első lépése: nézd végig az egész csomagot egyben.
          A rács a teljes csomagot mutatja, a szűrőtől függetlenül. */}
      {spreadOpen && (
        <div className="spread" role="dialog" aria-label={`${pack.name} — az egész csomag`}>
          <div className="spread-bar">
            <b>{pack.name}</b>
            <span>
              {spreadPicks.length
                ? `${spreadPicks.length} lap kiválasztva`
                : `${packCards.length} lap — koppints azokra, amelyek megszólítanak`}
            </span>
            {spreadPicks.length > 0 && (
              <>
                <button className="btn" onClick={() => setSpreadPicks([])}>Kijelölés törlése</button>
                <button className="btn btn-primary" onClick={() => arrangeOnBoard(spreadPicks)}>
                  <LayoutGrid size={16} /> Elrendezem a táblán ({spreadPicks.length})
                </button>
              </>
            )}
            <button className="btn btn-icon" onClick={() => setSpreadOpen(false)} aria-label="Bezárás">
              <X size={16} />
            </button>
          </div>
          <div className="spread-grid">
            {packCards.map((image, index) => {
              const picked = spreadPicks.some((p) => p.src === image.src);
              const pickIndex = spreadPicks.findIndex((p) => p.src === image.src);
              return (
                <button
                  key={image.id}
                  className={`thumb${picked ? ' picked' : ''}`}
                  data-src={image.src}
                  aria-pressed={picked}
                  aria-label={`${pack.name} kártya, ${index + 1}.`}
                  onClick={() => setSpreadPicks((prev) => {
                    if (prev.some((p) => p.src === image.src)) {
                      return prev.filter((p) => p.src !== image.src);
                    }
                    if (prev.length >= MAX_FREE) {
                      showToast(`Legfeljebb ${MAX_FREE} lap fér a táblára`);
                      return prev;
                    }
                    return [...prev, image];
                  })}
                >
                  <img src={thumbSrc(image.src)} alt="" loading="lazy" draggable={false} />
                  {picked && <span className="pick-num">{pickIndex + 1}</span>}
                  <span
                    className={`fav${favorites.has(image.src) ? ' on' : ''}`}
                    role="button"
                    tabIndex={-1}
                    aria-label="Csillagozás"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => { e.stopPropagation(); toggleFavorite(image.src); }}
                  >
                    <Star size={13} fill={favorites.has(image.src) ? 'currentColor' : 'none'} />
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {toast && <div className="toast" role="status">{toast}</div>}
    </div>
  );
};

export default App;
